import { apiClient, type ApiResponse } from './client';

export type WorkCenterType = 'MACHINE' | 'LABOR' | 'BOTH' | (string & {});

export interface WorkCenterPayload {
    /** Short code (WC-001); empty on create = next code in the series */
    code?: string;
    name: string;
    type: WorkCenterType;
    /** Legacy units/hour; no longer entered in the form (the server keeps the current value) */
    capacityPerHour?: number;
    warehouseId?: string | null;
    description?: string;
    isActive?: boolean;
    /** Production / Quality / Maintenance cost center (department grouping); null clears it */
    costCenterId?: string | null;
    /** Real capacity in hours per day */
    workingHoursPerDay?: number;
    /** Shifts the work center runs */
    shiftIds?: string[];
    /** Productive share of the working hours, % */
    efficiencyPct?: number;
    /** Machines on this work center (exactly these; others are taken off it) */
    machineIds?: string[];
    /** Manual work center: people needed at a time; null on machine work centers */
    personsRequired?: number | null;
    /** Manual work center: standard labour rate per person per hour (₹) */
    stdLabourRate?: number | null;
    /** Machine + labour: each machine's operators counted as the TOTAL of their wages or their AVG */
    operatorWageBasis?: 'TOTAL' | 'AVG';
}

export interface WorkCenterShift {
    id: string;
    name: string;
    start_time: string;
    end_time: string;
    break_minutes: number;
    working_days: string[];
}

export interface WorkCenterResponse {
    id: string;
    code: string;
    name: string;
    type: WorkCenterType;
    capacity_per_hour: number;
    warehouse_id: string | null;
    description: string | null;
    is_active: boolean;
    created_at: string;
    updated_at: string;
    cost_center_id: string | null;
    cost_center_name?: string | null;
    cost_center_code?: string | null;
    working_hours_per_day: number | string;
    efficiency_pct: number | string;
    shift_ids: string[];
    shifts?: WorkCenterShift[];
    /** From the work center's type: routing stages on it need a machine */
    machine_required?: boolean;
    type_label?: string | null;
    machines?: {
        id: string;
        name: string;
        machine_code: string | null;
        status?: string;
        is_active?: boolean;
        machine_hour_rate?: number | string;
        /** Wages of the operators linked to the machine: their total and their average (₹/hr) */
        operator_total_wage?: number | string;
        operator_avg_wage?: number | string;
    }[];
    persons_required?: number | null;
    std_labour_rate?: number | string | null;
    operator_wage_basis?: 'TOTAL' | 'AVG';
    machine_count?: number;
    /** Standard hourly cost (₹/hr) and its parts, worked out by the server */
    std_labour_cost_per_hour?: number;
    std_operator_cost_per_hour?: number;
    std_machine_cost_per_hour?: number;
    std_hourly_cost?: number;
}

export interface WorkCenterTypeDef {
    code: string;
    label: string;
    machineRequired: boolean;
    isSystem: boolean;
    usedBy: number;
}

const BASE = '/api/v1/work-centers';

/** Cost center, hours, shifts and efficiency in the API's field names; fields not given are left out. */
function planningFields(payload: Partial<WorkCenterPayload>) {
    return {
        ...(payload.costCenterId !== undefined ? { cost_center_id: payload.costCenterId || null } : {}),
        ...(payload.workingHoursPerDay !== undefined ? { working_hours_per_day: payload.workingHoursPerDay } : {}),
        ...(payload.shiftIds !== undefined ? { shift_ids: payload.shiftIds } : {}),
        ...(payload.efficiencyPct !== undefined ? { efficiency_pct: payload.efficiencyPct } : {}),
        ...(payload.machineIds !== undefined ? { machine_ids: payload.machineIds } : {}),
        ...(payload.personsRequired !== undefined ? { persons_required: payload.personsRequired } : {}),
        ...(payload.stdLabourRate !== undefined ? { std_labour_rate: payload.stdLabourRate } : {}),
        ...(payload.operatorWageBasis !== undefined ? { operator_wage_basis: payload.operatorWageBasis } : {}),
    };
}

export async function createWorkCenter(
    payload: WorkCenterPayload,
): Promise<ApiResponse<WorkCenterResponse>> {
    const formattedPayload = {
        code: payload.code?.trim() || undefined,
        name: payload.name,
        type: payload.type,
        capacity_per_hour: payload.capacityPerHour,
        warehouse_id: payload.warehouseId || null,
        description: payload.description || null,
        is_active: payload.isActive,
        ...planningFields(payload),
    };
    const { data } = await apiClient.post<ApiResponse<WorkCenterResponse>>(
        `${BASE}/create`,
        formattedPayload,
    );
    return data;
}

export async function getAllWorkCenters(warehouseId?: string): Promise<
    ApiResponse<WorkCenterResponse[]>
> {
    const params = warehouseId ? { warehouse_id: warehouseId } : undefined;
    const { data } = await apiClient.get<ApiResponse<WorkCenterResponse[]>>(
        `${BASE}/`,
        { params },
    );
    return data;
}

export async function updateWorkCenter(
    id: string,
    payload: Partial<WorkCenterPayload>,
): Promise<ApiResponse<WorkCenterResponse>> {
    const formattedPayload = {
        code: payload.code?.trim() || undefined,
        name: payload.name,
        type: payload.type,
        capacity_per_hour: payload.capacityPerHour,
        warehouse_id: payload.warehouseId !== undefined ? (payload.warehouseId || null) : undefined,
        description: payload.description !== undefined ? (payload.description || null) : undefined,
        is_active: payload.isActive,
        ...planningFields(payload),
    };
    const { data } = await apiClient.put<ApiResponse<WorkCenterResponse>>(
        `${BASE}/${id}`,
        formattedPayload,
    );
    return data;
}

/** Next code in the WC-001 series, to pre-fill the form (assigned for real on save). */
export async function getNextWorkCenterCode(): Promise<string> {
    const { data } = await apiClient.get<ApiResponse<{ code: string }>>(`${BASE}/next-code`);
    return data.data?.code ?? '';
}

export async function deleteWorkCenter(id: string): Promise<ApiResponse<null>> {
    const { data } = await apiClient.delete<ApiResponse<null>>(`${BASE}/${id}`);
    return data;
}

// ─── Work center types (Machine / Labor / Both + custom) ─────────────────────
export async function getWorkCenterTypes(): Promise<WorkCenterTypeDef[]> {
    const { data } = await apiClient.get<ApiResponse<WorkCenterTypeDef[]>>(`${BASE}/types`);
    return data.data ?? [];
}

export async function createWorkCenterType(label: string, machineRequired: boolean): Promise<WorkCenterTypeDef> {
    const { data } = await apiClient.post<ApiResponse<WorkCenterTypeDef>>(`${BASE}/types`, { label, machineRequired });
    return data.data as WorkCenterTypeDef;
}

export async function updateWorkCenterType(
    code: string,
    patch: { label?: string; machineRequired?: boolean },
): Promise<WorkCenterTypeDef> {
    const { data } = await apiClient.put<ApiResponse<WorkCenterTypeDef>>(`${BASE}/types/${encodeURIComponent(code)}`, patch);
    return data.data as WorkCenterTypeDef;
}
